import moment from 'moment/moment';
import { AuthTypes } from '../../auth/AuthTypes.js';
import { PostTypes } from '../postType/PostTypesEnum.js';
import _ from 'lodash';

/**
 * Handle common post actions
 */
export class PostService {
	/**
	 * Save injections
	 * @param $q
	 * @param Api
	 * @param Cfg
	 * @param modal
	 * @param Theme
	 */
	constructor($q, Api, Cfg, modal, Theme) {
		'ngInject';
		this.$q = $q;
		this.Api = Api;
		this.Cfg = Cfg;
		this.Theme = Theme;
		this.modal = modal;
		this.messenger = new window.CrossWindowMessenger(window.parent, '*');
		this.modalHeadingLabel = this.Cfg.readAsBool('ResearchManagement.enabled') ? 'Project' : 'Post';
	}

	/**
	 * Open a modal to create a post
	 * @return Promise  Resolves when modal closes
	 */
	create() {
		return this.$q((resolve, reject) => {
			return this.modal
				.state('hub.loggedin.PostCreate', {
					heading: `Create ${this.modalHeadingLabel}`,
					width: 768,
					height: 647,
					padding: false,
				})
				.then(resolve, reject);
		});
	}

	/**
	 * Open a modal to edit a post
	 * @param Object
	 * @property id
	 * @property external_ref  Used as part of the modal header in research management hubs
	 * @return Promise  Resolves when modal closes
	 */
	edit({ id, external_ref }) {
		return this.$q((resolve, reject) => {
			return this.modal
				.state('hub.loggedin.PostEdit', {
					params: { id },
					heading:
						this.modalHeadingLabel === 'Post'
							? `Edit ${this.modalHeadingLabel}`
							: `${this.modalHeadingLabel} #${external_ref}`,
					width: 750,
					height: 647,
					padding: false,
				})
				.then(resolve, reject);
		});
	}

	translationViewOriginal(postId, languageCode) {
		return this.$q((resolve, reject) => {
			return this.modal
				.state('hub.loggedin.TranslationOriginal', {
					params: { id: postId, language: languageCode },
					heading: 'Original Text',
					width: 675,
					height: 577 - 62,
					padding: false,
				})
				.then(resolve, reject);
		});
	}

	/**
	 * Open a modal to unarchive a post
	 * @return Promise  Resolves when modal closes
	 */
	unarchive(postId) {
		return this.$q((resolve, reject) => {
			return this.modal
				.state('hub.loggedin.PostUnarchive', {
					params: { id: postId },
					heading: 'Unarchive Post',
					width: 675,
					height: 577 - 62,
					padding: false,
				})
				.then(resolve, reject);
		});
	}

	/**
	 * Use modal to confirm delete then delete post
	 * @param post  Object with properties headline and id
	 */
	confirmDelete(post) {
		let escapedHtml = `
			Are you sure you want to delete this post?
			<br><br>
			<strong>${_.escape(post.headline)}</strong>
		`;

		// console.log('escapedHtml', { escapedHtml });
		return this.modal.confirm({ escapedHtml }).then(
			() => {
				this.Api.delete('/posts/' + post.id);
				post.deleted_at = moment().format();
				this.messenger.post('delete', { postId: post.id });
			},
			() => {}
		);
	}

	/**
	 * Use modal to confirm archive then archive post
	 * @param post  Object with properties headline and id
	 * @return Promise Resolves when post has been archived
	 */
	confirmArchive(post) {
		let areYouSure = `
			Are you sure you want to archive this post?
			<br><br>
			<strong>${_.escape(post.headline)}</strong>
		`;
		return this.$q((resolve, reject) => {
			return this.modal.confirm({ escapedHtml: areYouSure }).then(() => {
				this.Api.patch(`/posts/${post.id}/archive`).then(resolve, reject);
				this.messenger.post('archive', { postId: post.id });
			}, reject);
		});
	}

	/**
	 * Attach the given file to the given post
	 * @param post  Object with property id
	 * @param attachment  Object with properties name and id
	 */
	attachFile(post, attachment) {
		return this.Api.post(`/posts/${post.id}/attachments`, {
			name: attachment.name,
			attachment_id: attachment.id,
		});
	}

	/**
	 * Launch a react page inside an iframe
	 * @param restoreTheme Function  See `const restoreTheme` in this file.
	 * @param url String the URL to the react page
	 * @param modalOptions Object
	 *  @property modalOptions.heading String  The iframe modal's heading
	 * @returns {*}
	 */
	openReactIframe(restoreTheme, url, modalOptions = {}) {
		return this.$q((resolve, reject) => {
			return this.modal
				.iframe(url, {
					heading: modalOptions.heading,
					width: modalOptions.width ?? 'full',
					height: modalOptions.height ?? 'full',
				})
				.then(
					() => {
						restoreTheme();
						resolve();
					},
					() => {
						restoreTheme();
						reject();
					}
				);
		});
	}

	/**
	 * View the given post in fullscreen mode
	 * @param post  Object with property id, type, and external_ref
	 */
	viewFullscreen(post) {
		// if we are at a story board, also record click in story_board_clicks table
		const board = AuthTypes.getBoardHash();
		if (board) {
			const params = {
				post_id: post.id,
			};
			//record story click
			this.Api.post(`/story_boards/${board}/click`, params);
		}
		// the PostDetail modal updates the theme in case this is multihub
		// and the modal's post is from a different hub with different colors
		// and logo. Once the modal closes, we have to restore theme data
		// so the modal post's theme doesn't affect the current hub's theme
		const themeData = this.Theme.data();
		const restoreTheme = () => {
			this.Theme.apply(themeData);
		};
		if (post.type.id === PostTypes.research_brief) {
			const url = `${window.BASE_URL}/core/posts/${post.id}`;
			const heading = `Research Brief #${post.external_ref}`;
			this.openReactIframe(restoreTheme, url, { modalHeading: heading });
		} else {
			return this.$q((resolve, reject) => {
				return this.modal
					.state('hub.PostDetail', {
						heading: 'Post Details',
						params: { id: post.id },
					})
					.then(
						() => {
							restoreTheme();
							resolve();
						},
						() => {
							restoreTheme();
							reject();
						}
					);
			});
		}
	}

	/**
	 * Open a modal with some help text to inform user project is now complete.
	 */
	viewJtiCompletePost() {
		// the PostDetail modal updates the theme in case this is multihub
		// and the modal's post is from a different hub with different colors
		// and logo. Once the modal closes, we have to restore theme data
		// so the modal post's theme doesn't affect the current hub's theme
		const themeData = this.Theme.data();
		const restoreTheme = () => {
			this.Theme.apply(themeData);
		};

		const url = `${window.BASE_URL}/core/posts/complete`;
		const options = {
			width: 647,
			height: 200,
			heading: 'Project Complete',
		};
		this.openReactIframe(restoreTheme, url, options);
	}
}
